import { Row, Col, message, Drawer, Spin, Avatar, Button, Modal, Badge, Tooltip, Radio } from "antd";
import { ThunderboltOutlined } from "@ant-design/icons";
import React, { FC, useEffect, useState } from "react";
import { IApplicant, IProjectDetails } from "../helper";
import ApplicantsList from "./applicantsList";
import { axiosInstant, endpoints } from "../../../api";
import { showError } from "../../../Notifications";
import { IJob } from "../helper";
import { FilePdfOutlined, LinkedinOutlined, EyeOutlined, CommentOutlined } from "@ant-design/icons";
import RemarksPanel from "../../../Components/RemarksPanel";
import { useChatUnreadCounts } from "../../../Components/common/useChatUnreadCounts";
import { useUser } from "src/context/auth.context";

interface Props {
  job?: IJob;
}
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getResumeUrl = (resume: string) => {
  if (!resume) return "";
  if (resume.startsWith("http")) return resume;
  return `${API_BASE_URL}${resume}`;
};

const getProfilePhotoUrl = (photo: string) => {
  if (!photo) return "";
  if (photo.startsWith("http")) return photo;
  return `${API_BASE_URL}${photo}`;
};

const managerStatusDisplay = {
  submitted:             { label: 'Submitted', color: 'purple' },
  shortlisted_manager:   { label: 'Shortlisted', color: 'green' },
  rejected_manager:      { label: 'Rejected', color: 'red' },
  approved_manager:      { label: 'Approved', color: 'success' },
  interviewing_manager:  { label: 'Interview Scheduled', color: 'blue' },
  pending_manager:       { label: 'Pending Review', color: 'default' },
  hired:                 { label: 'Hired', color: 'gold' },
  offer_placed:          { label: 'Offer Placed', color: 'gold' },
  offer_accepted:        { label: 'Offer Accepted', color: 'green' },
  offer_rejected:        { label: 'Offer Rejected', color: 'red' },
  offer_revoked:         { label: 'Offer Revoked', color: 'magenta' },
  offer_expired:         { label: 'Offer Expired', color: 'gray' },

  // ...add others as needed
};

export const ApplicantsListPanel: FC<Props> = ({ job }) => {
  const [jobApplications, setJobApplications] = useState<Array<IApplicant>>([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerProfile, setDrawerProfile] = useState<any>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState<string | null>(null);
  const [feedbackModal, setFeedbackModal] = useState<{ visible: boolean; applicationId?: string; feedback?: string; isReject?: boolean }>({ visible: false });
  const [feedbackValue, setFeedbackValue] = useState("");
  const [remarksPanel, setRemarksPanel] = useState<{ visible: boolean; jobApplicationId?: string }>({ visible: false });
  const [generatingFeedback, setGeneratingFeedback] = useState(false);
  const [feedbackDecision, setFeedbackDecision] = useState("neutral");
  const [feedbackNotes, setFeedbackNotes] = useState("");
  const [feedbackLocked, setFeedbackLocked] = useState(false);
  const [selectedJobApplicationId, setSelectedJobApplicationId] = useState("");
  const [selectedCandidateId, setSelectedCandidateId] = useState("");
  const [viewOfferModal, setViewOfferModal] = useState<{ visible: boolean; record?: IApplicant }>({ visible: false });
  // Unread messages in the chat with the recruiter, per application.
  const unreadCount = useChatUnreadCounts('recruiter-manager');
  const [chatJobApplicationId, setChatJobApplicationId] = useState<string | undefined>(undefined);
  const [chatModalVisible, setChatModalVisible] = useState(false);
  const { user } = useUser();

  // Listen for openChat events (from notification clicks)
  useEffect(() => {
    const handleOpenChat = (event: any) => {
      const { chatType, jobApplicationId, candidateId } = event.detail || {};
      console.log('🔔 openChat event received in manager dashboard:', { chatType, jobApplicationId, candidateId });
      
      // For manager dashboard, we primarily handle recruiter-manager chats
      if (chatType === 'recruiter-manager' && jobApplicationId) {
        console.log('✅ Opening recruiter-manager chat for:', jobApplicationId);
        setChatJobApplicationId(jobApplicationId);
        setChatModalVisible(true);
      }
    };

    window.addEventListener('openChat', handleOpenChat);
    
    return () => {
      window.removeEventListener('openChat', handleOpenChat);
    };
  }, []);

  const fetchApplications = async () => {
    if (!job?._id) return;
    setLoading(true);
    try {
      const res = await axiosInstant.get(`/job-applications/applications/by-job/${job?._id}`);

      const data = res.data;
      const visibleStatuses = ["submitted", "shortlisted_manager", "rejected_manager", "approved_manager", "interviewing_manager", "pending_manager", "hired", "offer_placed", "offer_accepted", "offer_rejected", "offer_revoked", "offer_expired"];
      const visible = (Array.isArray(data) ? data : []).filter(app => visibleStatuses.includes(app.status));
      const mapped = visible.map(app => ({
        _id: app._id,
        name: app.profile?.firstName && app.profile?.lastName
          ? `${app.profile.firstName} ${app.profile.lastName}`
          : app.profile?.firstName || app.profile?.lastName || app.candidate?.name || "",
        email: app.profile?.email || app.candidate?.email || "",
        position: app.positionTitle || app.position || "",
        resume: app.profile?.resume || app.resume || "",
        notes: app.notes || "",
        hiddenFields: app.hiddenFields || [],
        answers: app.preScreeningAnswers || [],
        screeningScore: app.screeningScore ?? null,
        profilePicture: app.profile?.profilePictureUrl || app.candidate?.profilePictureUrl || "",
        linkedin: app.profile?.linkedin || app.candidate?.linkedin || "",
        remarks: app.remarks || [],
        status: app.status,
        profile: app.profile,
        candidate: app.profile,
        jobApplicationId: app._id,
        applicantId: app.candidate?._id || app.candidateId || app.profile?.userId,
        secondInterviewType: app.secondInterviewType,
        secondInterviewDate: app.secondInterviewDate,
        secondInterviewLink: app.secondInterviewLink,
        managerFeedback: app.managerFeedback || "",
        feedback: app.feedback,
        candidateId: app.candidate?._id || app.candidateId || app.profile?.userId,
        offerLetter: app.offerLetter || "",
        offerAccepted: app.offerAccepted,
        offerRejected: app.offerRejected,
        offerAcceptedDate: app.offerAcceptedDate || "",
        offerRejectedDate: app.offerRejectedDate || "",
      }));
      console.log('Mapped applicants:', mapped);
      setJobApplications(mapped);
    } catch {
      message.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!job?._id) return;
    setLoading(true);
    fetchApplications();
    
    // Check if we need to auto-open a chat from a notification
    const chatToOpen = sessionStorage.getItem('openChatOnLoad');
    if (chatToOpen) {
      try {
        const chatData = JSON.parse(chatToOpen);
        console.log('Auto-opening chat from notification:', chatData);
        
        // Clear the session storage
        sessionStorage.removeItem('openChatOnLoad');
        
        // Open the chat after a short delay to ensure the page is loaded
        setTimeout(() => {
          setSelectedJobApplicationId(chatData.jobApplicationId);
          setRemarksPanel({ visible: true, jobApplicationId: chatData.jobApplicationId });
        }, 1000);
      } catch (e) {
        console.error('Error parsing chat data from session storage:', e);
        sessionStorage.removeItem('openChatOnLoad');
      }
    }
  }, [job?._id]);

  const handleShortlist = async (applicationId: string) => {
    try {
      await axiosInstant.patch(`/job-applications/applications/${applicationId}/status`, {
        status: 'shortlisted_manager'
      });
    message.success('Candidate shortlisted!');
      // Optionally refetch applications
      fetchApplications();
    } catch (e) {
      message.error('Failed to shortlist candidate');
    }
  };

  const handleApprove = async (applicationId: string) => {
    setApproveLoading(applicationId);
    try {
      await axiosInstant.patch(`/job-applications/applications/${applicationId}/status`, {
        status: 'approved_manager',
      });
      message.success('Candidate approved — recruiter has been notified.');
      fetchApplications();
    } catch {
      message.error('Failed to approve candidate');
    } finally {
      setApproveLoading(null);
    }
  };

  const handleReject = (applicationId: string) => {
    setFeedbackValue("");
    setFeedbackLocked(false);
    setFeedbackDecision("no");
    setFeedbackNotes("");
    setFeedbackModal({ visible: true, applicationId, isReject: true });
  };

  const fetchProfileById = async (_id: string) => {
    setDrawerLoading(true);
    try {
      const res = await axiosInstant.get(`/candidates/${_id}`);
      console.log("Drawer profile data:", res.data);
      setDrawerProfile(res.data);
    } catch (e) {
      showError('Failed to fetch profile details');
      setDrawerProfile(null);
    } finally {
      setDrawerLoading(false);
    }
  };

  const openMoreInfo = (record: any) => {
    console.log("openMoreInfo called with:", record);
    setDrawerProfile(record);
    setDrawerVisible(true);
  };

  const openRemarks = (jobApplicationId: string) => {
    console.log("Opening remarks for jobApplicationId:", jobApplicationId);
    setRemarksPanel({ visible: true, jobApplicationId });
  };

  const openFeedback = (applicationId: string, feedback: string) => {
    setFeedbackModal({ visible: true, applicationId, feedback });
    setFeedbackValue(feedback);
    setFeedbackLocked(!!feedback);
    setFeedbackDecision("neutral");
    setFeedbackNotes("");
  };

  const handleGenerateFeedback = async () => {
    setGeneratingFeedback(true);
    const decisionLabels: Record<string, string> = {
      strong_yes: 'Strong Yes — highly recommended',
      yes: 'Yes — recommended',
      neutral: 'Neutral — could go either way',
      no: 'No — not recommended',
      strong_no: 'Strong No — not a fit',
    };
    const decisionLabel = decisionLabels[feedbackDecision] || feedbackDecision;
    const positiveDecision = ['strong_yes', 'yes'].includes(feedbackDecision);
    const negativeDecision = ['no', 'strong_no'].includes(feedbackDecision);
    const formData = new FormData();
    formData.append("applicantId", feedbackModal.applicationId!);
    formData.append("jobId", job?._id!);
    formData.append("strengths", positiveDecision ? `Decision: ${decisionLabel}. ${feedbackNotes}`.trim() : feedbackNotes);
    formData.append("weaknesses", negativeDecision ? `Decision: ${decisionLabel}. ${feedbackNotes}`.trim() : feedbackNotes);
    try {
      const res = await axiosInstant.post('/job-applications/ai/generate-feedback', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFeedbackValue(res.data.feedback);
    } catch (e) {
      showError('Failed to generate feedback');
    } finally {
      setGeneratingFeedback(false);
    }
  };

  const props = {
    applicants: jobApplications,
    job,
    jobId: job?._id,
    openRemarks: openRemarks,
    openMoreInfo,
    onStatusChange: (id: string, status: string) => {/* implement or call your handler */},
    openProfileDrawer: (profile: any) => openMoreInfo(profile),
    openFeedback: (id: string, feedback: string) => openFeedback(id, feedback),
    setViewOfferModal: setViewOfferModal
  };
  return (
    <>
      <Row>
        <Col span={24}>
          <ApplicantsList {...props} refetch={fetchApplications} openFeedback={openFeedback} openRemarks={openRemarks} setViewOfferModal={setViewOfferModal} unreadCounts={unreadCount} handleApprove={handleApprove} handleReject={handleReject} />
        </Col>
      </Row>  
    
      <Drawer
        title={
          <span style={{ fontSize: '20px', fontWeight: 600 }}>
            {(drawerProfile?.firstName && drawerProfile?.lastName)
              ? `${drawerProfile.firstName} ${drawerProfile.lastName}`
              : drawerProfile?.firstName || drawerProfile?.lastName || "Candidate Profile"}
          </span>
        }
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={450}
      >
        {drawerLoading ? (
          <Spin />
        ) : drawerProfile ? (() => {
          const hidden: string[] = drawerProfile.hiddenFields || [];
          const show = (field: string) => !hidden.includes(field);
          const p = drawerProfile.profile || drawerProfile;
          return (
            <div style={{ fontSize: 15 }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                <Avatar size={72} src={getProfilePhotoUrl(p.profilePhoto || drawerProfile.profilePicture)} style={{ flexShrink: 0 }}>
                  {(!p.profilePhoto && !drawerProfile.profilePicture) && `${(drawerProfile.name || drawerProfile.firstName || '?')[0].toUpperCase()}`}
                </Avatar>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{drawerProfile.name || `${drawerProfile.firstName || ''} ${drawerProfile.lastName || ''}`.trim()}</div>
                  {show('positionTitle') && p.positionTitle && <div style={{ color: '#666', marginTop: 2 }}>{p.positionTitle}</div>}
                  {show('yearsOfExperience') && p.yearsOfExperience != null && <div style={{ color: '#888', fontSize: 13, marginTop: 2 }}>🏅 {p.yearsOfExperience} yrs experience</div>}
                </div>
              </div>

              {/* Contact */}
              {(show('email') || show('phone') || show('linkedin') || show('location')) && (
                <div style={{ background: '#fafafa', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Contact Info</div>
                  {show('email') && drawerProfile.email && <div style={{ marginBottom: 4 }}>✉️ {drawerProfile.email}</div>}
                  {show('phone') && p.phoneNumber && <div style={{ marginBottom: 4 }}>📞 {p.phoneNumber}</div>}
                  {show('location') && (p.location || drawerProfile.location) && <div style={{ marginBottom: 4 }}>📍 {p.location || drawerProfile.location}</div>}
                  {show('linkedin') && (p.linkedin || drawerProfile.linkedin) && (
                    <a href={p.linkedin || drawerProfile.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
                      <LinkedinOutlined style={{ marginRight: 4 }} />LinkedIn Profile
                    </a>
                  )}
                </div>
              )}

              {/* Work Preferences */}
              {(show('jobPreference') || show('relocation') || show('isSearching')) && (p.jobPreference || p.relocation || p.isSearching != null) && (
                <div style={{ background: '#fafafa', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Work Preferences</div>
                  {show('jobPreference') && p.jobPreference && <div style={{ marginBottom: 4 }}>💼 <b>Preference:</b> {p.jobPreference}</div>}
                  {show('relocation') && p.relocation && <div style={{ marginBottom: 4 }}>🏠 <b>Relocation:</b> {p.relocation}</div>}
                  {show('isSearching') && p.isSearching != null && (
                    <div style={{ marginBottom: 4 }}>
                      🔍 <b>Job status:</b> {p.isSearching ? 'Actively seeking' : 'Open to opportunities'}
                    </div>
                  )}
                </div>
              )}

              {/* Resume */}
              {show('resume') && drawerProfile.resume && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Resume</div>
                  <Button type="primary" icon={<FilePdfOutlined />} href={getResumeUrl(drawerProfile.resume)} target="_blank" rel="noopener noreferrer" style={{ marginBottom: 10 }}>
                    Open Resume
                  </Button>
                  <iframe src={getResumeUrl(drawerProfile.resume)} title="Resume Preview" width="100%" height="380px" style={{ border: '1px solid #eee', borderRadius: 6, display: 'block' }} />
                </div>
              )}

              {/* AI score */}
              {show('screeningScore') && drawerProfile.screeningScore != null && (
                <div style={{ background: '#fafafa', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>AI Screening Score</div>
                  <span style={{ fontSize: 22, fontWeight: 700, color: drawerProfile.screeningScore >= 70 ? '#52c41a' : drawerProfile.screeningScore >= 50 ? '#fa8c16' : '#f5222d' }}>
                    {drawerProfile.screeningScore}%
                  </span>
                </div>
              )}

              {/* Pre-screening answers */}
              {show('preScreeningAnswers') && drawerProfile.answers?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Pre-screening Answers</div>
                  {drawerProfile.answers.map((a: any, i: number) => (
                    <div key={i} style={{ marginBottom: 8, background: '#fafafa', borderRadius: 6, padding: '8px 12px' }}>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{a.question}</div>
                      <div style={{ color: '#555', fontSize: 13, marginTop: 2 }}>{a.answer}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Recruiter notes */}
              {show('recruiterNotes') && drawerProfile.notes && (
                <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Recruiter Notes</div>
                  <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{drawerProfile.notes}</div>
                </div>
              )}

              {/* Manager's own feedback — shown for reference after submitting */}
              {(drawerProfile.feedback || drawerProfile.managerFeedback) && (
                <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Your Feedback</div>
                  <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', color: '#333' }}>{drawerProfile.feedback || drawerProfile.managerFeedback}</div>
                </div>
              )}

              {hidden.length > 0 && (
                <div style={{ color: '#bbb', fontSize: 12, textAlign: 'center', marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
                  🔒 Some sections were not shared by the recruiter
                </div>
              )}
            </div>
          );
        })() : (
          <p style={{ fontSize: '16px' }}>No profile data available.</p>
        )}
      </Drawer>

      <RemarksPanel
        jobApplicationId={remarksPanel.jobApplicationId}
        type="recruiter-manager"
        visible={remarksPanel.visible}
        onCancel={() => setRemarksPanel({ visible: false })}
      />

      {/* RemarksPanel for notification-triggered chats */}
      {chatModalVisible && chatJobApplicationId && (
        <RemarksPanel
          jobApplicationId={chatJobApplicationId}
          type="recruiter-manager"
          visible={chatModalVisible}
          onCancel={() => {
            setChatModalVisible(false);
            setChatJobApplicationId(undefined);
          }}
        />
      )}

      <Modal
        title="Interview Feedback"
        open={feedbackModal.visible}
        onOk={async () => {
          if (!feedbackLocked && feedbackValue.trim()) {
            if (feedbackModal.isReject) {
              await axiosInstant.patch(`/job-applications/applications/${feedbackModal.applicationId}/status`, {
                status: 'rejected_manager',
                feedback: feedbackValue,
              });
              message.success('Candidate rejected — recruiter has been notified.');
            } else {
              await axiosInstant.patch(`/job-applications/applications/${feedbackModal.applicationId}/manager-feedback`, {
                feedback: feedbackValue,
              });
              message.success('Feedback shared with recruiter!');
            }
            setFeedbackLocked(true);
            fetchApplications();
          }
          setFeedbackModal({ visible: false });
        }}
        onCancel={() => {
          setFeedbackModal({ visible: false });
          setFeedbackLocked(false);
        }}
        okText={feedbackModal.isReject ? "Reject & Notify Recruiter" : "Submit Feedback"}
        okButtonProps={{ danger: feedbackModal.isReject, disabled: feedbackLocked || !feedbackValue.trim() }}
        width={520}
      >
        <div style={{ padding: '4px 0' }}>
          {feedbackModal.isReject && (
            <div style={{ background: '#fff7e6', border: '1px solid #ffe58f', borderRadius: 6, padding: '8px 12px', marginBottom: 16, fontSize: 13, color: '#7c5700' }}>
              Feedback goes to the recruiter only — candidate will not be notified.
            </div>
          )}

          {/* Step 1: Decision */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 8, fontSize: 13 }}>Overall decision</label>
            <Radio.Group
              value={feedbackDecision}
              onChange={e => setFeedbackDecision(e.target.value)}
              buttonStyle="solid"
              disabled={feedbackLocked}
            >
              <Radio.Button value="strong_yes">Strong Yes</Radio.Button>
              <Radio.Button value="yes">Yes</Radio.Button>
              <Radio.Button value="neutral">Neutral</Radio.Button>
              <Radio.Button value="no">No</Radio.Button>
              <Radio.Button value="strong_no">Strong No</Radio.Button>
            </Radio.Group>
          </div>

          {/* Step 2: Quick notes */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: 13 }}>
              Quick notes <span style={{ fontWeight: 400, color: '#aaa' }}>(optional)</span>
            </label>
            <textarea
              value={feedbackNotes}
              onChange={e => setFeedbackNotes(e.target.value)}
              placeholder="Key observations from the interview..."
              rows={2}
              disabled={feedbackLocked}
              style={{ width: '100%', resize: 'vertical', borderRadius: 6, padding: 8, border: '1px solid #d9d9d9', fontSize: 14 }}
            />
          </div>

          {/* Step 3: AI generate */}
          <Button
            icon={<ThunderboltOutlined />}
            onClick={handleGenerateFeedback}
            loading={generatingFeedback}
            disabled={feedbackLocked}
            style={{ width: '100%', marginBottom: 14 }}
          >
            Generate Feedback with AI
          </Button>

          {/* Step 4: Review & edit */}
          {(feedbackValue || generatingFeedback) && (
            <div>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: 13 }}>
                AI-generated feedback <span style={{ fontWeight: 400, color: '#aaa' }}>(review & edit)</span>
              </label>
              <textarea
                value={feedbackValue}
                onChange={e => setFeedbackValue(e.target.value)}
                rows={5}
                disabled={feedbackLocked || generatingFeedback}
                style={{ width: '100%', resize: 'vertical', borderRadius: 6, padding: 8, border: '1px solid #d9d9d9', fontSize: 14 }}
                placeholder="AI feedback will appear here..."
              />
            </div>
          )}
        </div>
      </Modal>

      <Modal
        title="Offer Letter"
        open={viewOfferModal.visible}
        onCancel={() => setViewOfferModal({ visible: false, record: undefined })}
        footer={null}
      >
        {viewOfferModal.record && (
          <div>
            <b>Offer Letter:</b>
            <pre style={{ whiteSpace: 'pre-wrap' }}>{viewOfferModal.record.offerLetter}</pre>
            {viewOfferModal.record.offerAccepted && (
              <div style={{ color: "green", marginTop: 8 }}>
                Offer Accepted {viewOfferModal.record.offerAcceptedDate && `on ${new Date(viewOfferModal.record.offerAcceptedDate).toLocaleDateString()}`}
              </div>
            )}
            {viewOfferModal.record.offerRejected && (
              <div style={{ color: "red", marginTop: 8 }}>
                Offer Rejected {viewOfferModal.record.offerRejectedDate && `on ${new Date(viewOfferModal.record.offerRejectedDate).toLocaleDateString()}`}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};
export default ApplicantsListPanel;
